import { DisplayedSoilAmount, HeightDiffList } from '@/features/siteTop/types';
import { axios } from '@/lib/axios';
import { OutputErrorLog } from '@/lib/error';

// 1ページを取得する関数
const fetchSinglePage = async (
  url: string,
  params: Record<string, unknown>,
  paginationKey: number
): Promise<{ heightDiffList: HeightDiffList[]; data: DisplayedSoilAmount | null }> => {
  const response = await axios.get<DisplayedSoilAmount>(url, {
    params: {
      ...params,
      paginationKey,
    },
  });

  return {
    heightDiffList: response.data.heightDiffList || [],
    data: paginationKey === 0 ? response.data : null,
  };
};

// totalPageを取得して10ページずつ並列取得する関数
const fetchAllPagesInBatches = async (
  url: string,
  params: Record<string, unknown>
): Promise<DisplayedSoilAmount | null> => {
  // 最初のリクエストでtotalPageを取得
  const firstResponse = await axios.get<DisplayedSoilAmount>(url, {
    params: {
      ...params,
      paginationKey: 0,
    },
  });

  const firstPageData: DisplayedSoilAmount | null = firstResponse.data;
  const allHeightDiffList: HeightDiffList[] = [...(firstResponse.data.heightDiffList || [])];

  const { totalPage } = firstResponse.data;

  // totalPageが1以下の場合は1ページのみ
  if (!totalPage || totalPage <= 1) {
    return firstPageData;
  }

  const BATCH_SIZE = 20;

  // 1ページ目は既に取得済みなので、2ページ目から開始
  for (let i = 1; i < totalPage; i += BATCH_SIZE) {
    const batch: number[] = [];

    // 次の10ページのキーを生成
    for (let j = 0; j < BATCH_SIZE && i + j < totalPage; j += 1) {
      batch.push(i + j);
    }

    // バッチ内のページを並列で取得
    // eslint-disable-next-line no-await-in-loop
    const batchResults = await Promise.allSettled(
      batch.map((paginationKey) => fetchSinglePage(url, params, paginationKey))
    );

    // 結果を集約
    batchResults.forEach((result) => {
      if (result.status === 'fulfilled') {
        allHeightDiffList.push(...result.value.heightDiffList);
      }
    });
  }

  if (!firstPageData) {
    throw new Error('no data');
  }

  return {
    ...firstPageData,
    heightDiffList: allHeightDiffList,
  };
};

export const getDisplayedSoilAmountByTask = async ({
  siteId,
  taskId,
  dataFileId,
  meshSize,
}: {
  siteId: number;

  taskId: number;
  dataFileId: number;
  meshSize?: number;
}): Promise<DisplayedSoilAmount | null> => {
  try {
    return await fetchAllPagesInBatches(
      `/sites/${siteId}/soil-amount/tasks/${taskId}/data-file/${dataFileId}`,
      { meshSize }
    );
  } catch (error) {
    OutputErrorLog('getDisplayedSoilAmount', error);
    return null;
  }
};

export const getDisplayedSoilAmountByDate = async ({
  siteId,
  dataFileId,
  meshSize,
}: {
  siteId: number;
  dataFileId: number;
  meshSize?: number;
}): Promise<DisplayedSoilAmount | null> => {
  try {
    return await fetchAllPagesInBatches(`/sites/${siteId}/soil-amount/data-file/${dataFileId}`, {
      meshSize,
    });
  } catch (error) {
    OutputErrorLog('getDisplayedSoilAmount', error);
    return null;
  }
};
